import {useCallback, useEffect, useMemo, useState} from 'react';
import useScreenSize from '../../hooks/useScreenSize';
import {useDispatch, useSelector} from 'react-redux';
import {createMovement, deleteMovement, findAllMovements, updateMovement} from '../../api/moneyMovement';
import {findAllAccounts} from '../../api/capitalAccount';
import {findAllProjects} from '../../api/project';
import {findAllCompanies} from '../../api/company';
import {selectAccountsById, setAccounts} from '../../store/accounts';
import {selectAccountsSorted, selectProjectsSorted} from '../../store/selectors';
import {setProjects} from '../../store/projects';
import {setCompanies} from '../../store/companies';
import {formatGroupDate} from './utils/dateUtils';
import {computeSummary, groupByDate} from './utils/mathUtils';
import {
    initIncome, incomeToForm, buildIncomePayload,
    initExpense, expenseToForm, buildExpensePayload,
    initTransfer, transferToForm, buildTransferPayload,
} from './utils/movementUtils';
import TransactionFilter from '../../components/common/TransactionFilter';
import Modal from './components/Modal';
import TransactionItem from './components/TransactionItem';
import IncomeForm from './components/IncomeForm';
import ExpenseForm from './components/ExpenseForm';
import TransferForm from './components/TransferForm';
import './TransactionsPage.scss';
import Loader from '../../components/common/Loader';
import TransactionFilterButton
    from '../../components/common/TransactionFilter/TransactionFilterButton';

const syncRelated = (dispatch) =>
    Promise.all([findAllAccounts(), findAllProjects(), findAllCompanies()])
        .then(([accounts, projects, companies]) => {
            dispatch(setAccounts(accounts));
            dispatch(setProjects(projects));
            dispatch(setCompanies(companies));
        });

const TransactionsPage = () => {
    const dispatch = useDispatch();
    const {isSidebarOpen, isCompact} = useScreenSize();
    const accounts = useSelector(selectAccountsSorted);
    const accountsById = useSelector(selectAccountsById);
    const projects = useSelector(selectProjectsSorted);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(null);
    const [filterOpen, setFilterOpen] = useState(isSidebarOpen);

    const [modal, setModal] = useState(null); // null | 'income' | 'expense' | 'transfer'
    const [editItem, setEditItem] = useState(null);
    const [incomeForm, setIncomeForm] = useState(initIncome());
    const [expenseForm, setExpenseForm] = useState(initExpense());
    const [transferForm, setTransferForm] = useState(initTransfer());
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, type}

    const onFiltersChange = useCallback(f => setFilters(f), []);

    const loadItems = useCallback((f) => {
        setLoading(true);
        findAllMovements({
            from: f.from,
            to: f.to,
            accountIds: f.accountIds,
            projectIds: f.projectIds,
            types: f.types,
            searchText: f.searchText || undefined,
        })
            .then(setItems)
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (filters) loadItems(filters);
    }, [filters, loadItems]);

    const openCreate = (type) => {
        setEditItem(null);
        if (type === 'income') setIncomeForm(initIncome());
        else if (type === 'transfer') setTransferForm(initTransfer());
        else setExpenseForm(initExpense());
        setModal(type);
    };

    const openEdit = (item) => {
        setEditItem(item);
        if (item.type === 'INCOME') {
            setIncomeForm(incomeToForm(item));
            setModal('income');
        } else if (item.type === 'TRANSFER') {
            setTransferForm(transferToForm(item));
            setModal('transfer');
        } else {
            setExpenseForm(expenseToForm(item));
            setModal('expense');
        }
    };

    const closeModal = () => { setModal(null); setEditItem(null); };

    const handleSaveIncome = async () => {
        setSaving(true);
        try {
            const payload = buildIncomePayload(incomeForm);
            if (editItem) await updateMovement(editItem.id, payload);
            else await createMovement(payload);
            closeModal();
            if (filters) loadItems(filters);
            syncRelated(dispatch);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveExpense = async () => {
        setSaving(true);
        try {
            const payload = buildExpensePayload(expenseForm);
            if (editItem) await updateMovement(editItem.id, payload);
            else await createMovement(payload);
            closeModal();
            if (filters) loadItems(filters);
            syncRelated(dispatch);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveTransfer = async () => {
        setSaving(true);
        try {
            const payload = buildTransferPayload(transferForm);
            if (editItem) await updateMovement(editItem.id, payload);
            else await createMovement(payload);
            closeModal();
            if (filters) loadItems(filters);
            syncRelated(dispatch);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const {id} = deleteConfirm;
        setDeleteConfirm(null);
        await deleteMovement(id);
        if (filters) loadItems(filters);
        syncRelated(dispatch);
    };

    const grouped = useMemo(() => groupByDate(items), [items]);

    return (
        <div className="capital-page">
            <div className="capital-page__header">
                <TransactionFilterButton onClick={() => setFilterOpen(v => !v)}/>
                {!isCompact && <h2 className="capital-page__title">Transactions</h2>}
                <div className="tx-toolbar__actions">
                    <button className="tx-btn tx-btn--income" onClick={() => openCreate('income')}>+ Income</button>
                    <button className="tx-btn tx-btn--expense" onClick={() => openCreate('expense')}>+ Expense</button>
                    <button className="tx-btn tx-btn--transfer" onClick={() => openCreate('transfer')}>+ Transfer</button>
                </div>
            </div>

            <div className="capital-page__body">
                <div className="tx-page-layout">
                    <TransactionFilter
                        accounts={accounts}
                        projects={projects}
                        onFiltersChange={onFiltersChange}
                        expanded={filterOpen}
                        onExpandedChange={setFilterOpen}
                    />

                    <div className="tx-content">
                        {/* Stream */}
                        {loading ? (
                            <Loader/>
                        ) : grouped.length === 0 ? (
                            <p className="tx-empty">No transactions found for the selected period.</p>
                        ) : (
                            <div className="tx-stream">
                                {grouped.map(group => (
                                    <div key={group.date} className="tx-date-group">
                                        <div className="tx-date-header">{formatGroupDate(group.date)}</div>
                                        {group.items.map(item => (
                                            <TransactionItem
                                                key={`${item.type}-${item.id}`}
                                                item={item}
                                                accountsById={accountsById}
                                                onEdit={openEdit}
                                                onDelete={item => setDeleteConfirm({id: item.id, type: item.type})}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {modal === 'income' && (
                <Modal onClose={closeModal}>
                    <IncomeForm
                        form={incomeForm}
                        onChange={setIncomeForm}
                        accounts={accounts}
                        projects={projects}
                        editId={editItem?.id}
                        onSubmit={handleSaveIncome}
                        onCancel={closeModal}
                        saving={saving}
                    />
                </Modal>
            )}

            {modal === 'expense' && (
                <Modal onClose={closeModal}>
                    <ExpenseForm
                        form={expenseForm}
                        onChange={setExpenseForm}
                        accounts={accounts}
                        editId={editItem?.id}
                        onSubmit={handleSaveExpense}
                        onCancel={closeModal}
                        saving={saving}
                    />
                </Modal>
            )}

            {modal === 'transfer' && (
                <Modal onClose={closeModal}>
                    <TransferForm
                        form={transferForm}
                        onChange={setTransferForm}
                        accounts={accounts}
                        editId={editItem?.id}
                        onSubmit={handleSaveTransfer}
                        onCancel={closeModal}
                        saving={saving}
                    />
                </Modal>
            )}

            {deleteConfirm && (
                <Modal onClose={() => setDeleteConfirm(null)}>
                    <div className="tx-form">
                        <h3 className="tx-form__title">Delete {deleteConfirm.type === 'INCOME' ? 'income entry' : deleteConfirm.type === 'TRANSFER' ? 'transfer' : 'expense'}?</h3>
                        <p style={{margin: 0, fontSize: 13, color: 'var(--text-muted)'}}>This cannot be undone.</p>
                        <div className="tx-form__actions">
                            <button className="tx-form__btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                            <button className="tx-form__btn tx-form__btn--danger" onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default TransactionsPage;